import { helpWhy } from '../../help/why';
import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../context/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import { useToast } from '../../context/ToastContext';
import { ExpFormService } from '../../services/expForm.service';
import { PERMISSIONS } from '../../constants/permissions';
import { ICreateExpRealizationRequest, IExpForm, IExpRealization, IExpRealizationDeductionRequest } from '../../interfaces/commercial.interface';
import ProceedsDistributionGrid from './ProceedsDistributionGrid'; // W3-B2
import { toLocalIsoDate, formatDate } from '../../utils/dateUtils';

const money = (n: number) => (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const today = () => toLocalIsoDate();

const emptyForm = (isBdt: boolean): ICreateExpRealizationRequest => ({
    realizationDate: today(), amount: 0, exchangeRate: isBdt ? 1 : 0, prcNumber: '', remarks: '', bankCharges: 0,
});

interface Props {
    institutionId: string;
    exp: IExpForm;
    onChanged?: () => void;
}

/**
 * Realized vs outstanding for one EXP, and the per-entry exchange rate (R-27). `exchangeRate` is
 * always required on the form; when the EXP's own currency is BDT it is pinned to exactly 1 and
 * the field is disabled, matching the server rule ("must be absent or exactly 1" for BDT).
 */
const ExpRealizationPanel: React.FC<Props> = ({ institutionId, exp, onChanged }) => {
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.EXP_MANAGE);
    const canViewJournal = usePermission(PERMISSIONS.JOURNAL_ENTRY_VIEW);
    const canViewLoans = usePermission(PERMISSIONS.FINANCING_VIEW); // W3-B2: loan-liquidation rows
    const [saving, setSaving] = useState(false); // double-click guard: a distributed save posts a journal
    const [deductions, setDeductions] = useState<IExpRealizationDeductionRequest[]>([]);
    const isBdt = exp.currencyCode === 'BDT';
    const [rows, setRows] = useState<IExpRealization[]>([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState<IExpRealization | null>(null);
    const [form, setForm] = useState<ICreateExpRealizationRequest>(emptyForm(isBdt));
    const [dialogOpen, setDialogOpen] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const res = await ExpFormService.listRealizations(institutionId, exp.id);
            setRows(res.content);
        } catch (e) {
            toast.showApiError(e, 'Failed to load realizations');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [institutionId, exp.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const openCreate = () => { setEditing(null); setForm(emptyForm(isBdt)); setDeductions([]); setDialogOpen(true); };
    const openEdit = (r: IExpRealization) => {
        setEditing(r);
        setDeductions([]);
        setForm({
            realizationDate: r.realizationDate, amount: r.amount, exchangeRate: r.exchangeRate,
            prcNumber: r.prcNumber ?? '', remarks: r.remarks ?? '', bankCharges: r.bankCharges ?? 0,
        });
        setDialogOpen(true);
    };

    // R-27, binding: exchange rate is required always, and for a BDT EXP it must be exactly 1.
    const valid = form.amount >= 0.01 && !!form.realizationDate
        && form.exchangeRate !== null && form.exchangeRate !== undefined && form.exchangeRate >= 0.000001
        && (!isBdt || form.exchangeRate === 1);

    const saveWhy = saving ? null
        : form.amount < 0.01 ? 'Enter the amount received, at least 0.01.'
        : !form.realizationDate ? 'Enter the realization date.'
        : form.exchangeRate === null || form.exchangeRate === undefined || form.exchangeRate < 0.000001 ? 'Enter an exchange rate above zero.'
        : isBdt && form.exchangeRate !== 1 ? 'For BDT the rate must be 1.'
        : null;

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);
        try {
            const payload: ICreateExpRealizationRequest = { ...form, exchangeRate: isBdt ? 1 : form.exchangeRate };
            if (!editing && deductions.length > 0) payload.deductions = deductions;
            if (editing) {
                await ExpFormService.updateRealization(institutionId, exp.id, editing.id, payload);
                toast.showSuccess('Realization updated');
            } else {
                const created = await ExpFormService.createRealization(institutionId, exp.id, payload);
                if (created.postingWarning) toast.showWarning(created.postingWarning);
                else toast.showSuccess('Realization recorded');
            }
            setDialogOpen(false);
            await load();
            onChanged?.();
        } catch (e) {
            toast.showApiError(e, 'Failed to save realization');
        } finally {
            setSaving(false);
        }
    };

    const handlePostLedger = async (r: IExpRealization) => {
        try {
            await ExpFormService.postRealizationToLedger(institutionId, exp.id, r.id);
            toast.showSuccess('Realization posted to the ledger');
            await load();
            onChanged?.();
        } catch (e) {
            toast.showApiError(e, 'Failed to post the realization to the ledger');
        }
    };

    const handleDelete = async (r: IExpRealization) => {
        try {
            await ExpFormService.deleteRealization(institutionId, exp.id, r.id);
            toast.showSuccess('Realization removed');
            await load();
            onChanged?.();
        } catch (e) {
            toast.showApiError(e, 'Failed to remove realization');
        }
    };

    return (
        <Paper data-help-section="commercial.realization.table" sx={{ p: 2, mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6">Realizations</Typography>
                {canManage && (
                    <Button data-help="commercial.realization.add" size="small" startIcon={<AddIcon />} onClick={openCreate}>Add Realization</Button>
                )}
            </Box>

            <Box sx={{ display: 'flex', gap: 3, mb: 2 }} data-testid="exp-realization-summary">
                <Typography variant="body2">Invoice Value: {exp.currencyCode} {money(exp.invoiceValue)}</Typography>
                <Typography variant="body2">Realized: {exp.currencyCode} {money(exp.realizedAmount)}</Typography>
                <Typography variant="body2">Outstanding: {exp.currencyCode} {money(exp.balanceValue)}</Typography>
            </Box>

            {canViewJournal && (exp.receivableJournalEntryId || exp.writeOffJournalEntryId) && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }} data-testid="exp-journal-links">
                    {exp.receivableJournalEntryId && (
                        <Link component={RouterLink} to={`/accounting/journal-entries/${exp.receivableJournalEntryId}`}
                            variant="body2" data-testid="exp-shipment-journal-link">Shipment journal</Link>
                    )}
                    {exp.writeOffJournalEntryId && (
                        <Link component={RouterLink} to={`/accounting/journal-entries/${exp.writeOffJournalEntryId}`}
                            variant="body2" data-testid="exp-writeoff-journal-link">Write-off journal</Link>
                    )}
                </Box>
            )}

            {!loading && (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="commercial.realization.col.date">Date</TableCell>
                                <TableCell align="right" data-help="commercial.realization.col.amount">Amount</TableCell>
                                <TableCell align="right" data-help="commercial.realization.col.rate">Exchange Rate</TableCell>
                                <TableCell align="right" data-help="commercial.realization.col.cleared">Receivable cleared (BDT)</TableCell>
                                <TableCell align="right" data-help="commercial.realization.col.fx">FX gain / (loss) (BDT)</TableCell>
                                <TableCell align="right" data-help="commercial.realization.col.net">Net to bank (BDT)</TableCell>
                                <TableCell data-help="commercial.realization.col.prc">PRC No.</TableCell>
                                {canViewJournal && <TableCell data-help="commercial.realization.col.journal">Journal</TableCell>}
                                {canManage && <TableCell align="right" data-help="commercial.realization.col.actions">Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(r => (
                                <TableRow key={r.id}>
                                    <TableCell>{formatDate(r.realizationDate)}</TableCell>
                                    <TableCell align="right">{money(r.amount)}</TableCell>
                                    <TableCell align="right" data-testid={`exp-realization-rate-${r.id}`}>{r.exchangeRate}</TableCell>
                                    <TableCell align="right" data-testid={`exp-realization-cleared-${r.id}`}>
                                        {r.receivableClearedBase == null ? '—' : money(r.receivableClearedBase)}
                                    </TableCell>
                                    <TableCell align="right" data-testid={`exp-realization-fx-${r.id}`}
                                        sx={{ color: r.fxGainLossBase != null && r.fxGainLossBase < 0 ? 'error.main' : undefined }}>
                                        {r.fxGainLossBase == null ? '—'
                                            : r.fxGainLossBase < 0 ? `(${money(Math.abs(r.fxGainLossBase))})` : money(r.fxGainLossBase)}
                                    </TableCell>
                                    <TableCell align="right" data-testid={`exp-realization-net-${r.id}`}>
                                        {r.netToBank == null ? '—' : money(r.netToBank)}
                                    </TableCell>
                                    <TableCell>{r.prcNumber ?? '—'}</TableCell>
                                    {canViewJournal && (
                                        <TableCell>
                                            {r.journalEntryId ? (
                                                <Link component={RouterLink} to={`/accounting/journal-entries/${r.journalEntryId}`}
                                                    variant="body2" data-testid={`exp-realization-journal-link-${r.id}`}>View</Link>
                                            ) : '—'}
                                        </TableCell>
                                    )}
                                    {canManage && (
                                        <TableCell align="right">
                                            {exp.receivableBooked && !r.journalEntryId && Number(r.amount) > 0 && (
                                                <Button data-help="commercial.realization.postLedger" size="small" color="warning" onClick={() => handlePostLedger(r)}
                                                    data-testid={`exp-realization-post-ledger-${r.id}`}>Post to ledger</Button>
                                            )}
                                            {!(r.deductions && r.deductions.length > 0) && (
                                                <IconButton data-help="commercial.realization.edit" size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton>
                                            )}
                                            <IconButton data-help="commercial.realization.remove" size="small" onClick={() => handleDelete(r)}><DeleteIcon fontSize="small" /></IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={7 + (canManage ? 1 : 0) + (canViewJournal ? 1 : 0)} align="center">No realizations recorded yet.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ 'data-help-page': 'commercial.realization.dialog.page' } as any}>
                <DialogTitle>{editing ? 'Edit Realization' : 'Add Realization'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField data-help="commercial.realization.date" label="Realization Date" type="date" InputLabelProps={{ shrink: true }} required
                        value={form.realizationDate} onChange={e => setForm({ ...form, realizationDate: e.target.value })} />
                    <TextField data-help="commercial.realization.amount" label="Amount" type="number" required
                        value={form.amount} onChange={e => setForm({ ...form, amount: Number(e.target.value) })} />
                    <TextField
                        data-help="commercial.realization.rate" label="Exchange Rate" type="number" required
                        value={form.exchangeRate}
                        disabled={isBdt}
                        helperText={isBdt ? 'Fixed at 1 for a BDT EXP' : `Taka (BDT) per 1 ${exp.currencyCode} on this inward remittance${exp.receivableBooked && exp.bookingRate ? ` (booked at ${exp.bookingRate}; the difference posts as realised FX gain/loss)` : ''}`}
                        onChange={e => setForm({ ...form, exchangeRate: Number(e.target.value) })}
                    />
                    {isBdt && <Alert severity="info">This EXP is in BDT — the exchange rate must be exactly 1.</Alert>}
                    <TextField
                        data-help="commercial.realization.charges" label="Bank Charges" type="number"
                        value={form.bankCharges ?? 0}
                        helperText="Optional, in Taka (BDT) -- deducted from the realized amount when posting to GL"
                        onChange={e => setForm({ ...form, bankCharges: Number(e.target.value) })}
                    />
                    <TextField data-help="commercial.realization.prc" label="PRC Number" value={form.prcNumber ?? ''}
                        onChange={e => setForm({ ...form, prcNumber: e.target.value })} />
                    <TextField data-help="commercial.realization.remarks" label="Remarks" multiline minRows={2} value={form.remarks ?? ''}
                        onChange={e => setForm({ ...form, remarks: e.target.value })} />
                    {!editing && (
                        <ProceedsDistributionGrid institutionId={institutionId} exp={exp} base={form}
                            rows={deductions} onChange={setDeductions} canViewLoans={canViewLoans} />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button data-help="commercial.realization.cancel" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button data-help="commercial.realization.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="contained" disabled={!valid || saving} onClick={handleSave}>Save</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default ExpRealizationPanel;
